import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Link2, Check, Loader2, Users, Search, UserPlus, 
  AlertCircle, X, CreditCard, Eye, EyeOff, ChevronRight, ChevronDown,
  Sparkles, Ban, RefreshCw, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Find Match Modal - Search CRM customers to link
const FindMatchModal = ({ stripeCustomer, onClose, onLink }) => {
  const [matches, setMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [includeLinked, setIncludeLinked] = useState(false);

  useEffect(() => {
    loadMatches();
  }, [includeLinked]);

  const loadMatches = async (search = '') => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      let url = `${API_URL}/api/integrations/stripe/customers/${stripeCustomer.id}/matches`;
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (includeLinked) params.append('include_linked', 'true');
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setMatches(data.matches || []);
      }
    } catch (err) {
      console.error('Error loading matches:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadMatches(searchTerm);
  };

  const handleLink = async (crmCustomerId) => {
    setIsLinking(true);
    await onLink(stripeCustomer.id, crmCustomerId);
    setIsLinking(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 safe-area-all">
      <div className="bg-white rounded-3xl w-full max-w-2xl modal-shell-h flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-lg">Find Matching Customer</h3>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
              <X size={20} />
            </button>
          </div>
          
          {/* Stripe Customer Card */}
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#635bff] flex items-center justify-center text-white font-bold text-lg">
                S
              </div>
              <div className="flex-1">
                <p className="font-semibold">{stripeCustomer.name || 'No name'}</p>
                <p className="text-white/70 text-sm">{stripeCustomer.email || 'No email'}</p>
              </div>
              <div className="text-right text-xs text-white/60">
                <p>{stripeCustomer.id}</p>
                {stripeCustomer.has_payment_method && (
                  <p className="flex items-center gap-1 mt-1">
                    <CreditCard size={12} />
                    Card on file
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="p-4 border-b border-zinc-100">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <button 
              type="submit"
              className="px-5 py-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 font-medium transition-colors"
            >
              Search
            </button>
          </div>
          
          {/* Include already linked toggle */}
          <label className="flex items-center gap-2 mt-3 text-sm text-zinc-600 cursor-pointer">
            <input
              type="checkbox"
              checked={includeLinked}
              onChange={(e) => setIncludeLinked(e.target.checked)}
              className="w-4 h-4 rounded border-zinc-300 text-emerald-500 focus:ring-emerald-500"
            />
            Include customers already linked to other Stripe accounts
          </label>
        </form>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Users size={48} className="mx-auto mb-3 opacity-30" />
              <p>No matching customers found</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="space-y-3">
              {matches.map((match) => {
                const isAlreadyLinked = match.already_linked || match.stripe_customer_id;
                return (
                  <div 
                    key={match.id}
                    className={`border rounded-xl p-4 transition-all ${
                      isAlreadyLinked 
                        ? 'border-amber-300 bg-amber-50/50' 
                        : 'border-zinc-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-semibold ${
                          isAlreadyLinked 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {match.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900">{match.name}</p>
                          <p className="text-sm text-zinc-500">{match.email || match.address || 'No details'}</p>
                          {isAlreadyLinked && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <AlertCircle size={12} />
                              Already linked to another Stripe customer
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {match.match_score > 0 && (
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            match.match_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                            match.match_score >= 50 ? 'bg-amber-100 text-amber-700' :
                            'bg-zinc-100 text-zinc-600'
                          }`}>
                            {match.match_score}% match
                          </span>
                        )}
                        {isAlreadyLinked ? (
                          <span className="px-4 py-2 bg-zinc-200 text-zinc-500 text-sm rounded-xl font-medium flex items-center gap-2">
                            <Ban size={14} />
                            Linked
                          </span>
                        ) : (
                          <button
                            onClick={() => handleLink(match.id)}
                            disabled={isLinking}
                            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-zinc-300 text-white text-sm rounded-xl font-medium transition-colors flex items-center gap-2"
                          >
                            {isLinking ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                            Link
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Sync Page
const StripeCustomerSync = ({ onBack, onViewCustomer }) => {
  const [stripeCustomers, setStripeCustomers] = useState([]);
  const [crmCustomers, setCrmCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [linkingCustomer, setLinkingCustomer] = useState(null);
  const [importingId, setImportingId] = useState(null);
  const [ignoringId, setIgnoringId] = useState(null);
  const [unlinkingId, setUnlinkingId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastCustomerId, setLastCustomerId] = useState(null);
  const [filter, setFilter] = useState('pending'); // pending, linked, ignored, all
  const [ignoredIds, setIgnoredIds] = useState(new Set());
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncAllArmed, setSyncAllArmed] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showMatchPreview, setShowMatchPreview] = useState(false);
  const [isLinkingAll, setIsLinkingAll] = useState(false);
  const [isRefreshingPaymentStatus, setIsRefreshingPaymentStatus] = useState(false);

  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    setLoadingProgress(0);
    
    try {
      const token = api.getToken();
      
      // Fetch ALL Stripe customers by paginating through all pages
      let allCustomers = [];
      let hasMorePages = true;
      let startingAfter = null;
      
      while (hasMorePages) {
        const url = startingAfter 
          ? `${API_URL}/api/integrations/stripe/customers?limit=100&starting_after=${startingAfter}`
          : `${API_URL}/api/integrations/stripe/customers?limit=100`;
          
        const stripeRes = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const stripeData = await stripeRes.json();
        
        if (!stripeRes.ok) {
          throw new Error(stripeData.detail || 'Failed to load Stripe customers');
        }
        
        allCustomers = [...allCustomers, ...(stripeData.customers || [])];
        hasMorePages = stripeData.has_more;
        
        if (stripeData.customers?.length > 0) {
          startingAfter = stripeData.customers[stripeData.customers.length - 1].id;
        }
        
        // Update loading progress
        setLoadingProgress(allCustomers.length);
      }
      
      // Sort by created date (most recent first) - already sorted by backend
      setStripeCustomers(allCustomers);
      setHasMore(false);
      
      // Load CRM customers for linking
      const crmRes = await fetch(`${API_URL}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (crmRes.ok) {
        const crmData = await crmRes.json();
        setCrmCustomers(crmData || []);
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = () => {
    // Not used anymore but kept for compatibility
  };

  const handleImport = async (stripeCustomerId) => {
    setImportingId(stripeCustomerId);
    setError(null);
    
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/integrations/stripe/customers/${stripeCustomerId}/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to import customer');
      }
      
      setStripeCustomers(prev => 
        prev.map(c => c.id === stripeCustomerId ? { ...c, is_linked: true } : c)
      );
      
      setSuccessMessage('Customer created and linked!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setImportingId(null);
    }
  };

  const handleLink = async (stripeCustomerId, crmCustomerId) => {
    setError(null);
    
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/integrations/stripe/customers/${stripeCustomerId}/link`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ crm_customer_id: crmCustomerId })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to link customer');
      }
      
      setStripeCustomers(prev => 
        prev.map(c => c.id === stripeCustomerId ? { ...c, is_linked: true } : c)
      );
      
      setLinkingCustomer(null);
      setSuccessMessage('Customer linked successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      setError(err.message);
    }
  };

  const handleIgnore = (stripeCustomerId) => {
    setIgnoredIds(prev => new Set([...prev, stripeCustomerId]));
    setSuccessMessage('Customer skipped');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const handleUnlink = async (stripeCustomerId) => {
    setUnlinkingId(stripeCustomerId);
    setError(null);
    
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/integrations/stripe/customers/${stripeCustomerId}/unlink`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to unlink customer');
      }
      
      // Update local state to reflect the unlink
      setStripeCustomers(prev => 
        prev.map(c => c.id === stripeCustomerId 
          ? { ...c, is_linked: false, linked_customer_id: null, linked_customer_name: null } 
          : c
        )
      );
      
      setSuccessMessage('Customer unlinked successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setUnlinkingId(null);
    }
  };

  const handleUndoIgnore = (stripeCustomerId) => {
    setIgnoredIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(stripeCustomerId);
      return newSet;
    });
  };

  // Sync all pending Stripe customers (auto-link matches or import as new)
  const handleSyncAllMatches = async () => {
    // Two-step confirm: window.confirm is unreliable in iOS PWA standalone.
    if (!syncAllArmed) {
      setSyncAllArmed(true);
      toast.warning(`Tap Sync All again to confirm`, {
        description: `This will auto-link or import all ${stats.pending} pending Stripe customers.`,
        duration: 6000,
      });
      setTimeout(() => setSyncAllArmed(false), 6000);
      return;
    }
    setSyncAllArmed(false);

    setIsSyncingAll(true);
    setError(null);

    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/integrations/stripe/customers/sync-all`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to sync customers');
      }
      
      setSuccessMessage(`Synced ${data.imported_count || 0} customers successfully!`);
      toast.success(`Synced ${data.imported_count || 0} customers`);
      setTimeout(() => setSuccessMessage(null), 5000);
      
      // Reload the data to reflect changes
      await loadData();
      
    } catch (err) {
      console.error('Error syncing all customers:', err);
      setError(err.message);
      toast.error(`Sync failed: ${err.message}`);
    } finally {
      setIsSyncingAll(false);
    }
  };

  // Refresh payment method status for all linked CRM customers
  const handleRefreshPaymentStatus = async () => {
    setIsRefreshingPaymentStatus(true);
    setError(null);
    
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/integrations/stripe/customers/refresh-payment-status`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to refresh payment status');
      }
      
      setSuccessMessage(`Updated ${data.updated_count} customers: ${data.with_payment_method} with cards, ${data.without_payment_method} without`);
      setTimeout(() => setSuccessMessage(null), 5000);
      
      // Reload the data to reflect changes
      await loadData();
      
    } catch (err) {
      console.error('Error refreshing payment status:', err);
      setError(err.message);
    } finally {
      setIsRefreshingPaymentStatus(false);
    }
  };

  // Filter customers based on selected filter
  const filteredCustomers = stripeCustomers.filter(c => {
    // First apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch = (
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.id?.toLowerCase().includes(term)
      );
      if (!matchesSearch) return false;
    }
    
    // Then apply status filter
    const isIgnored = ignoredIds.has(c.id);
    
    switch (filter) {
      case 'pending':
        return !c.is_linked && !isIgnored;
      case 'linked':
        return c.is_linked;
      case 'ignored':
        return isIgnored;
      case 'all':
      default:
        return true;
    }
  });

  // Stats
  const stats = {
    total: stripeCustomers.length,
    linked: stripeCustomers.filter(c => c.is_linked).length,
    pending: stripeCustomers.filter(c => !c.is_linked && !ignoredIds.has(c.id)).length,
    ignored: ignoredIds.size,
    withCards: stripeCustomers.filter(c => c.has_payment_method).length
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mx-auto mb-3" />
          <p className="text-zinc-600">Loading Stripe customers...</p>
          {loadingProgress > 0 && (
            <p className="text-zinc-500 text-sm mt-2">{loadingProgress} customers loaded</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="stripe-customer-sync-page">
      {/* Header - with safe area padding */}
      <div className="bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 text-white" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={onBack}
                data-testid="back-btn"
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold">Stripe Customer Sync</h1>
                <p className="text-zinc-400 text-sm">Link your Stripe customers to StreakFree</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefreshPaymentStatus}
                disabled={isRefreshingPaymentStatus}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 rounded-xl font-medium transition-colors disabled:opacity-50"
                title="Check which linked customers have saved cards"
              >
                {isRefreshingPaymentStatus ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <CreditCard size={18} />
                )}
                Update Cards
              </button>
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors"
              >
                <RefreshCw size={18} />
                Refresh
              </button>
            </div>
          </div>
        </div>
        
        {/* Stats Bar */}
        <div className="px-6 pb-5">
          <div className="grid grid-cols-4 gap-3">
            <button
              onClick={() => setFilter('pending')}
              className={`p-4 rounded-2xl transition-all ${
                filter === 'pending' 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className={`text-sm ${filter === 'pending' ? 'text-amber-100' : 'text-zinc-400'}`}>Pending</p>
            </button>
            
            <button
              onClick={() => setFilter('linked')}
              className={`p-4 rounded-2xl transition-all ${
                filter === 'linked' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <p className="text-2xl font-bold">{stats.linked}</p>
              <p className={`text-sm ${filter === 'linked' ? 'text-emerald-100' : 'text-zinc-400'}`}>Linked</p>
            </button>
            
            <button
              onClick={() => setFilter('ignored')}
              className={`p-4 rounded-2xl transition-all ${
                filter === 'ignored' 
                  ? 'bg-zinc-600 text-white' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <p className="text-2xl font-bold">{stats.ignored}</p>
              <p className={`text-sm ${filter === 'ignored' ? 'text-zinc-300' : 'text-zinc-400'}`}>Skipped</p>
            </button>
            
            <button
              onClick={() => setFilter('all')}
              className={`p-4 rounded-2xl transition-all ${
                filter === 'all' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className={`text-sm ${filter === 'all' ? 'text-purple-100' : 'text-zinc-400'}`}>Total</p>
            </button>
          </div>
          
          {/* Review All Matches Button */}
          {stats.pending > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setShowMatchPreview(true)}
                data-testid="review-matches-btn"
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-2xl transition-all shadow-lg"
              >
                <Eye size={22} />
                Review All Matches ({stripeCustomers.filter(c => c.potential_match && !c.potential_match.already_linked && !c.is_linked).length} found)
              </button>
              <p className="text-center text-zinc-400 text-xs mt-2">
                Preview all suggested matches before linking
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 flex items-center gap-3">
              <AlertCircle size={20} />
              <p>{error}</p>
              <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-100 rounded-lg">
                <X size={16} />
              </button>
            </div>
          )}
          
          {successMessage && (
            <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-700 flex items-center gap-3">
              <Check size={20} />
              <p>{successMessage}</p>
            </div>
          )}

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or Stripe ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
              className="w-full pl-12 pr-4 py-4 border border-zinc-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white shadow-sm text-lg"
            />
          </div>

          {/* Customer Cards */}
          <div className="space-y-4">
            {filteredCustomers.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-zinc-200">
                <Users size={48} className="mx-auto mb-4 text-zinc-300" />
                <p className="text-zinc-500 text-lg">No customers to show</p>
                <p className="text-zinc-400 text-sm mt-1">
                  {filter === 'pending' && 'All customers have been processed!'}
                  {filter === 'linked' && 'No linked customers yet'}
                  {filter === 'ignored' && 'No skipped customers'}
                </p>
              </div>
            ) : (
              filteredCustomers.map((customer) => {
                const isIgnored = ignoredIds.has(customer.id);
                const suggestedMatches = customer.suggested_matches || [];
                const hasGoodMatch = customer.potential_match && !customer.potential_match.already_linked;
                
                // Format last transaction date
                const formatDate = (timestamp) => {
                  if (!timestamp) return null;
                  const date = new Date(timestamp * 1000);
                  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
                };
                
                return (
                  <div 
                    key={customer.id}
                    data-testid={`customer-card-${customer.id}`}
                    className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${
                      customer.is_linked ? 'border-emerald-200 bg-emerald-50/30' :
                      isIgnored ? 'border-zinc-200 opacity-60' :
                      hasGoodMatch ? 'border-purple-200 bg-purple-50/30' :
                      'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    {/* Customer Info */}
                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        {/* Stripe Icon */}
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#635bff] to-[#7c3aed] flex items-center justify-center text-white font-bold text-xl shadow-lg flex-shrink-0">
                          S
                        </div>
                        
                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h3 className="font-semibold text-zinc-900 text-lg">
                                {customer.name || 'No name'}
                              </h3>
                              <p className="text-zinc-500 text-sm">{customer.email || 'No email'}</p>
                              {customer.address && (
                                <p className="text-zinc-400 text-sm mt-1 truncate">
                                  {typeof customer.address === 'string' 
                                    ? customer.address 
                                    : customer.address.line1 || ''}
                                </p>
                              )}
                            </div>
                            
                            {/* Status Badge */}
                            <div className="flex-shrink-0">
                              {customer.is_linked ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-sm font-medium rounded-full">
                                  <Check size={14} />
                                  Linked
                                </span>
                              ) : isIgnored ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-400 text-white text-sm font-medium rounded-full">
                                  <EyeOff size={14} />
                                  Skipped
                                </span>
                              ) : hasGoodMatch ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500 text-white text-sm font-medium rounded-full">
                                  <Sparkles size={14} />
                                  Match Found
                                </span>
                              ) : null}
                            </div>
                          </div>
                          
                          {/* Meta Info - Last Transaction & Card */}
                          <div className="flex flex-wrap items-center gap-2 mt-3 text-sm">
                            {customer.last_transaction_date && (
                              <span className="flex items-center gap-1 text-zinc-600 bg-zinc-100 px-2 py-1 rounded">
                                <CreditCard size={12} />
                                Last: {formatDate(customer.last_transaction_date)}
                                {customer.last_transaction_amount && (
                                  <span className="font-medium ml-1">£{customer.last_transaction_amount.toFixed(2)}</span>
                                )}
                              </span>
                            )}
                            {customer.has_payment_method && (
                              <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                                <CreditCard size={14} />
                                Card on file
                              </span>
                            )}
                          </div>
                          
                          {/* Suggested Matches Section */}
                          {!customer.is_linked && !isIgnored && suggestedMatches.length > 0 && (
                            <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
                              <p className="text-sm text-purple-700 font-semibold flex items-center gap-2 mb-2">
                                <Sparkles size={16} />
                                Suggested Matches ({suggestedMatches.length})
                              </p>
                              <div className="space-y-2">
                                {suggestedMatches.map((match, idx) => (
                                  <div 
                                    key={match.id} 
                                    className={`flex items-center justify-between p-2 rounded-lg ${
                                      match.already_linked 
                                        ? 'bg-amber-100/50 border border-amber-200' 
                                        : 'bg-white/80 border border-purple-200'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                        match.already_linked 
                                          ? 'bg-amber-200 text-amber-700' 
                                          : 'bg-purple-200 text-purple-700'
                                      }`}>
                                        {idx + 1}
                                      </span>
                                      <div>
                                        <p className="text-sm font-medium text-zinc-800">{match.name}</p>
                                        <p className="text-xs text-zinc-500">{match.email}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                        match.match_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                        match.match_score >= 50 ? 'bg-purple-100 text-purple-700' :
                                        'bg-zinc-100 text-zinc-600'
                                      }`}>
                                        {match.match_score}%
                                      </span>
                                      {match.already_linked && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-700 font-medium flex items-center gap-1">
                                          <AlertCircle size={10} />
                                          Linked
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {suggestedMatches.every(m => m.already_linked) && (
                                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                  <AlertCircle size={12} />
                                  All suggested matches are already linked to other Stripe customers
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    {!customer.is_linked && (
                      <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100">
                        {isIgnored ? (
                          <button
                            onClick={() => handleUndoIgnore(customer.id)}
                            className="w-full py-3 text-zinc-600 hover:text-zinc-900 font-medium rounded-xl hover:bg-white transition-colors flex items-center justify-center gap-2"
                          >
                            <Eye size={18} />
                            Undo Skip
                          </button>
                        ) : (
                          <div className="grid grid-cols-3 gap-3">
                            {/* Find & Link */}
                            <button
                              onClick={() => setLinkingCustomer(customer)}
                              data-testid={`find-btn-${customer.id}`}
                              className="py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                              <Link2 size={18} />
                              Link
                            </button>
                            
                            {/* Skip */}
                            <button
                              onClick={() => handleIgnore(customer.id)}
                              disabled={ignoringId === customer.id}
                              data-testid={`skip-btn-${customer.id}`}
                              className="py-3 bg-zinc-200 hover:bg-zinc-300 text-zinc-700 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                              <Ban size={18} />
                              Skip
                            </button>
                            
                            {/* Create New */}
                            <button
                              onClick={() => handleImport(customer.id)}
                              disabled={importingId === customer.id}
                              data-testid={`create-btn-${customer.id}`}
                              className="py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-zinc-300 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                              {importingId === customer.id ? (
                                <Loader2 size={18} className="animate-spin" />
                              ) : (
                                <>
                                  <UserPlus size={18} />
                                  Create
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Linked Customer Info - show View Customer and Unlink buttons */}
                    {customer.is_linked && customer.linked_customer_id && (
                      <div className="px-5 py-3 bg-emerald-50 border-t border-emerald-100 flex items-center justify-between">
                        <button
                          onClick={() => handleUnlink(customer.id)}
                          disabled={unlinkingId === customer.id}
                          data-testid={`unlink-btn-${customer.id}`}
                          className="text-red-500 hover:text-red-700 text-sm font-medium flex items-center gap-1 px-3 py-1.5 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          {unlinkingId === customer.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <X size={14} />
                          )}
                          Unlink
                        </button>
                        <button
                          onClick={() => {
                            if (typeof onViewCustomer === 'function') {
                              onViewCustomer(customer.linked_customer_id);
                            }
                          }}
                          data-testid={`view-customer-btn-${customer.id}`}
                          className="text-emerald-600 hover:text-emerald-800 text-sm font-medium flex items-center gap-1"
                        >
                          View Customer
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
            
            {/* Load More Button */}
            {hasMore && !isLoading && (
              <div className="text-center py-6">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  data-testid="load-more-btn"
                  className="px-8 py-3 bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-400 text-white font-medium rounded-xl transition-colors flex items-center gap-2 mx-auto"
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    <>
                      <ChevronDown size={18} />
                      Load More Customers
                    </>
                  )}
                </button>
                <p className="text-zinc-400 text-sm mt-2">
                  Showing {stripeCustomers.length} customers
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Match Preview Modal - shows ALL matches before linking */}
      {showMatchPreview && (
        <MatchPreviewModal
          stripeCustomers={stripeCustomers}
          onClose={() => setShowMatchPreview(false)}
          onLinkAll={async (matchesToLink) => {
            setIsLinkingAll(true);
            setShowMatchPreview(false);
            
            try {
              const token = api.getToken();
              // Bulk endpoint expects a raw JSON array of
              // {stripe_customer_id, crm_customer_id}
              const links = matchesToLink.map(match => ({
                stripe_customer_id: match.stripeId,
                crm_customer_id: match.crmId
              }));
              
              const res = await fetch(`${API_URL}/api/integrations/stripe/customers/bulk-link`, {
                method: 'POST',
                headers: { 
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(links)
              });
              
              const result = await res.json();
              
              if (res.ok) {
                // Update local state for all successfully linked customers
                const failedStripeIds = (result.errors || []).map(e => e.link?.stripe_customer_id).filter(Boolean);
                const linkedStripeIds = matchesToLink
                  .map(m => m.stripeId)
                  .filter(id => !failedStripeIds.includes(id));
                
                setStripeCustomers(prev => 
                  prev.map(c => linkedStripeIds.includes(c.id) ? { ...c, is_linked: true } : c)
                );
                
                // Check saved cards for the newly linked customers so the
                // Stripe badge appears on the customer list (matches the old
                // auto-sync behaviour).
                setSuccessMessage(`Linked ${result.linked_count} customers — checking cards on file...`);
                let cardsFound = 0;
                try {
                  for (let i = 0; i < 12; i++) {
                    const cardRes = await fetch(`${API_URL}/api/integrations/stripe/customers/refresh-cards?limit=100`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!cardRes.ok) break;
                    const cardData = await cardRes.json();
                    cardsFound += cardData.with_card || 0;
                    if (!cardData.remaining) break;
                  }
                } catch (e) {
                  console.error('Card check failed:', e);
                }
                
                setSuccessMessage(`Linked ${result.linked_count} customers (${cardsFound} with cards on file)${result.errors?.length > 0 ? `, ${result.errors.length} failed` : ''}`);
              } else {
                setError(typeof result.detail === 'string' ? result.detail : 'Failed to link customers');
              }
            } catch (err) {
              console.error('Error bulk linking customers:', err);
              setError('Failed to link customers. Please try again.');
            }
            
            setIsLinkingAll(false);
            setTimeout(() => setSuccessMessage(null), 5000);
          }}
        />
      )}

      {/* Linking All Progress Overlay */}
      {isLinkingAll && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
            <p className="text-lg font-semibold text-zinc-900">Linking customers...</p>
            <p className="text-zinc-500 text-sm mt-1">Please wait while we link all matches</p>
          </div>
        </div>
      )}

      {/* Find Match Modal */}
      {linkingCustomer && (
        <FindMatchModal
          stripeCustomer={linkingCustomer}
          onClose={() => setLinkingCustomer(null)}
          onLink={handleLink}
        />
      )}
    </div>
  );
};

// Match Preview Modal Component
const MatchPreviewModal = ({ stripeCustomers, onClose, onLinkAll }) => {
  const [selectedMatches, setSelectedMatches] = useState(new Set());
  
  // Get all customers with good matches (not already linked)
  const customersWithMatches = stripeCustomers.filter(c => 
    !c.is_linked && 
    c.potential_match && 
    !c.potential_match.already_linked
  );
  
  // Initialize all as selected
  useEffect(() => {
    setSelectedMatches(new Set(customersWithMatches.map(c => c.id)));
  }, []);
  
  const toggleMatch = (stripeId) => {
    setSelectedMatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stripeId)) {
        newSet.delete(stripeId);
      } else {
        newSet.add(stripeId);
      }
      return newSet;
    });
  };
  
  const selectAll = () => {
    setSelectedMatches(new Set(customersWithMatches.map(c => c.id)));
  };
  
  const selectNone = () => {
    setSelectedMatches(new Set());
  };
  
  const handleLinkSelected = () => {
    const matchesToLink = customersWithMatches
      .filter(c => selectedMatches.has(c.id))
      .map(c => ({
        stripeId: c.id,
        stripeName: c.name || c.email,
        crmId: c.potential_match.id,
        crmName: c.potential_match.name,
        score: c.potential_match.match_score
      }));
    
    onLinkAll(matchesToLink);
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 safe-area-all">
      <div className="bg-white rounded-3xl w-full max-w-3xl modal-shell-h flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Review All Matches</h2>
              <p className="text-purple-200 text-sm mt-1">
                {customersWithMatches.length} Stripe customers matched to CRM records
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        {/* Selection Controls */}
        <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={selectAll}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium"
            >
              Select All
            </button>
            <button 
              onClick={selectNone}
              className="text-sm text-zinc-500 hover:text-zinc-700 font-medium"
            >
              Select None
            </button>
          </div>
          <p className="text-sm text-zinc-600">
            <span className="font-semibold text-purple-600">{selectedMatches.size}</span> of {customersWithMatches.length} selected
          </p>
        </div>
        
        {/* Match List */}
        <div className="flex-1 overflow-y-auto p-4">
          {customersWithMatches.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Users size={48} className="mx-auto mb-3 opacity-30" />
              <p>No automatic matches found</p>
              <p className="text-sm mt-1">You can manually link customers from the main list</p>
            </div>
          ) : (
            <div className="space-y-2">
              {customersWithMatches.map((customer) => {
                const isSelected = selectedMatches.has(customer.id);
                const match = customer.potential_match;
                
                return (
                  <div 
                    key={customer.id}
                    onClick={() => toggleMatch(customer.id)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-zinc-200 bg-white hover:border-zinc-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Checkbox */}
                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected 
                          ? 'bg-purple-500 border-purple-500' 
                          : 'border-zinc-300'
                      }`}>
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                      
                      {/* Stripe Customer */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-[#635bff] flex items-center justify-center text-white text-xs font-bold">
                            S
                          </div>
                          <div className="truncate">
                            <p className="font-medium text-zinc-900 truncate">{customer.name || 'No name'}</p>
                            <p className="text-xs text-zinc-500 truncate">{customer.email || customer.id}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      <div className="text-zinc-400">
                        <ChevronRight size={20} />
                      </div>
                      
                      {/* CRM Match */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                            {match.name?.charAt(0) || '?'}
                          </div>
                          <div className="truncate">
                            <p className="font-medium text-zinc-900 truncate">{match.name}</p>
                            <p className="text-xs text-zinc-500 truncate">{match.email || 'CRM Customer'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Match Score */}
                      <div className={`px-3 py-1 rounded-full text-sm font-medium flex-shrink-0 ${
                        match.match_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                        match.match_score >= 50 ? 'bg-purple-100 text-purple-700' :
                        'bg-zinc-100 text-zinc-600'
                      }`}>
                        {match.match_score}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-200 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 text-zinc-600 hover:text-zinc-800 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleLinkSelected}
            disabled={selectedMatches.size === 0}
            className="px-8 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-300 text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
          >
            <Link2 size={18} />
            Link {selectedMatches.size} Selected
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripeCustomerSync;
